import React, { memo, useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Button, Dropdown, Modal, Radio } from 'sfy-react'
import { getRequestAsync, postRequestAsync } from '../../genericAPIs/index'
import { setToLocalStorage } from '../../../utils/localStorage'
import { setLoader, setTranslatedData, setLanguageCode, setSnackbar, setAppConfigDetails } from '../../../redux/common'
import { useSelector, useDispatch } from 'react-redux'
import { ReactComponent as Language } from '../../../assets/images/language.svg'
import { ReactComponent as Search } from '../../../assets/images/search.svg'
import styles from './ChangeLanguage.module.scss'

const ChangeLanguage = ({ classContainer = '', showLanguageIcon = true, customIcon }) => {
  const dispatch = useDispatch()

  const languageCode = useSelector((state) => state.common.languageCode)
  const translatedData = useSelector((state) => state.common.translatedData)

  const [showModal, setShowModal] = useState(false)
  const [selectedLanguageISO, setSelectedLanguageISO] = useState()
  const [buttonDisabled, setButtonDisabled] = useState()
  const [languagesData, setLanguagesData] = useState([])

  useEffect(() => {
    setSelectedLanguageISO({ code: languageCode.code, text: languageCode.text })
  }, [languageCode])

  const closePreferenceModal = useCallback(() => {
    setShowModal(false)
  }, [])

  const getAppConfigDetails = useCallback(async () => {
    const response = await getRequestAsync('/Consumer/appConfig')
    dispatch(setAppConfigDetails(response))
  }, [dispatch])

  const getTranslationDetails = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      setToLocalStorage('languageCode', { code: selectedLanguageISO.code, text: selectedLanguageISO.text })
      const response = await postRequestAsync('/LanguagesText/translations/consumer', {
        Source: 'Consumer',
      })
      dispatch(setTranslatedData(response))
      dispatch(setLanguageCode({ code: selectedLanguageISO.code, text: selectedLanguageISO.text }))
      setToLocalStorage('languageCode', { code: selectedLanguageISO.code, text: selectedLanguageISO.text })
      dispatch(setLoader(false))
    } catch (e) {
      console.log(e)
      dispatch(setSnackbar({ message: e.msg, type: 'error' }))
      dispatch(setLoader(false))
    }
  }, [dispatch, selectedLanguageISO])

  const setLanguage = useCallback(async () => {
    try {
      setShowModal(false)
      dispatch(setLoader(true))
      await getAppConfigDetails()
      await getTranslationDetails()
      dispatch(setLoader(false))
    } catch (e) {
      console.log(e)
      dispatch(setSnackbar({ message: e.msg, type: 'error' }))
      dispatch(setLoader(false))
    }
  }, [dispatch, getAppConfigDetails, getTranslationDetails])

  const onChangeLanguageModalOpen = async (event) => {
    event.preventDefault()
    try {
      dispatch(setLoader(true))
      const response = await getRequestAsync('/list/languages')
      setLanguagesData(response)
      if (response.length) {
        setShowModal(true)
        setSelectedLanguageISO({ code: languageCode.code, text: languageCode.text })
        setToLocalStorage('languageCode', { code: languageCode.code, text: languageCode.text })
        dispatch(setLoader(false))
      } else {
        setShowModal(false)
        dispatch(setSnackbar({ message: translatedData.error.languageUnavailable, type: 'error' }))
        dispatch(setLoader(false))
      }
      setButtonDisabled(true)
    } catch (e) {
      console.log(e)
      setShowModal(false)
      dispatch(setSnackbar({ message: e.msg, type: 'error' }))
      dispatch(setLoader(false))
    }
  }

  const onLanguageClick = (event) => {
    const buttonDisabled = event.target.value === languageCode.code
    setButtonDisabled(buttonDisabled)
    setSelectedLanguageISO({ code: event.target.value, text: event.target.name })
  }

  return (
    <>
      <div className={`${styles['change-language-container']} ${classContainer}`} onClick={onChangeLanguageModalOpen}>
        {showLanguageIcon && (customIcon || <Language className={styles['language-icon']} />)}
        {languageCode.text}
      </div>
      {ReactDOM.createPortal(
        <Modal showModal={showModal} closeModal={closePreferenceModal} showCloseBtn className='default-modal'>
          <Modal.Header className='mb10 ml3'>
            <h4>{translatedData.common.changeLanguage}</h4>
          </Modal.Header>
          <Modal.Body>
            {languagesData.length > 0 && (
              <Dropdown
                id='change-language-dropdown'
                value={selectedLanguageISO.text || ''}
                options={languagesData.map((language) => ({ ...language, value: language.name }))}
                placeholder={translatedData.common.search}
                handleOnChange={(item) => {
                  setButtonDisabled(item.iso === languageCode.code)
                  setSelectedLanguageISO({ code: item.iso, text: item.name })
                }}
                classObject={{
                  container: `${styles['search-language-container']}`,
                }}
                filterBy='value'
                searchText='Search'
                showDownArrow={false}
              >
                <Search className={styles['search-icon']} />
              </Dropdown>
            )}
            {languagesData.map(({ name, iso }) => (
              <Radio
                key={iso}
                value={iso}
                name={name}
                selectedValue={selectedLanguageISO.code}
                handleOnUpdate={onLanguageClick}
                labelClasses={`f12 ${styles['radio-tabs']}`}
                classes={`${styles['radio-label']} ${selectedLanguageISO.code === iso ? styles['radio-label--active'] : ''}`}
              >
                {name}
              </Radio>
            ))}
          </Modal.Body>
          <Modal.Footer className='justify-center'>
            <Button classes='button-primary mt20' text={translatedData.common.continue} onClick={setLanguage} disabled={buttonDisabled} />
          </Modal.Footer>
        </Modal>,
        document.getElementById('portal-root')
      )}
    </>
  )
}

export default memo(ChangeLanguage)
